import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    Directive,
    Input,
    OnChanges,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { timer } from 'rxjs';
import { take } from 'rxjs/operators';

import {
    Context,
    Controller,
    RenderContext,
    Renderer,
    RendererOptions,
    TabsetNode,
} from '../insight/experimental.tabset';

@Directive({ selector: 'ng-template[tabsetContent]' })
export class TabsetContent {
    constructor(public templateRef: TemplateRef<any>) {}
}

@Directive({ selector: 'ng-template[tabContent]' })
export class TabContent {
    constructor(public templateRef: TemplateRef<any>) {}
}

@Component({
    selector: '[tabset]',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: '',
    exportAs: 'tabset',
})
export class TabsetComponent implements OnChanges, AfterContentInit {
    @ContentChild(TabsetContent) tabsetContent: TabsetContent;
    @ContentChild(TabContent) tabContent: TabContent;
    @Input() rendererOptions: RendererOptions;
    tabsetTpl: TemplateRef<any>;
    tabTpl: TemplateRef<any>;
    node: TabsetNode;
    renderer: Renderer;

    constructor(private readonly _viewContainerRef: ViewContainerRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('rendererOptions')) {
            this._render();
        }
    }

    ngAfterContentInit(): void {
        if (this.tabsetContent) {
            this.tabsetTpl = this.tabsetContent.templateRef;
        }
        if (this.tabContent) {
            this.tabTpl = this.tabContent.templateRef;
        }
        this._render();
    }

    private _render(): void {
        this._destroy();

        if (!this.rendererOptions || (!this.tabsetTpl && !this.tabTpl)) return;

        const render = (context: Context): RenderContext => {
            const tpl =
                context.type === 'tabset' ? this.tabsetTpl : this.tabTpl;
            if (!tpl) return;
            const ref = tpl.createEmbeddedView(context);
            return {
                element: ref.rootNodes[0],
                detectChanges: () => ref.detectChanges(),
                destroy: () => ref.destroy(),
            };
        };

        this.rendererOptions.defaultRenderFn = render;
        this.renderer = new Renderer(this.rendererOptions);
        this.node = this.renderer.tabset({});
        const controller = new Controller();
        controller.renderer = this.renderer;
        try {
            controller.tabsetNode = this.node;
            this.renderer.render(this.node);
            this._viewContainerRef.element.nativeElement.appendChild(
                this.node.renderContext.element,
            );
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            controller.layout().then(() => {
                timer(100)
                    .pipe(take(1))
                    .subscribe({
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async
                        complete: () => controller.autoSizeTabset(),
                    });
            });
        } catch (e) {
            console.log(e);
            this._destroy();
        }
    }

    private _destroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.node &&
            this.node.renderContext &&
            this.node.renderContext.element
        ) {
            this._viewContainerRef.element.nativeElement.removeChild(
                this.node.renderContext.element,
            );
            if (this.renderer) {
                this.renderer.destroy(this.node);
            }
        }
    }
}
